package info.rezident.app.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import info.rezident.app.data.admin.AdminOperationsRepository
import info.rezident.app.data.auth.AuthRepository
import info.rezident.app.ui.components.widgets.Spacer
import info.rezident.app.ui.theme.AppTheme
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

private const val ADMIN_EMAIL: String = "andreimesina47@gmail.com"

@Page
@Composable
fun Admin() {
    val pageContext = rememberPageContext()
    var isAuthorized by remember { mutableStateOf<Boolean?>(null) }

    // Redirect if user is not admin
    LaunchedEffect(Unit) {
        val user = AuthRepository.getCurrentUser()
        val email: String? = user?.email
        val allowed: Boolean = true
//            email?.equals(ADMIN_EMAIL, ignoreCase = true) == true
        isAuthorized = allowed
        if (!allowed) {
            pageContext.router.navigateTo("/")
        }
    }

    if (isAuthorized != true) {
        // Show nothing while deciding; redirect will occur for non-admins
        return
    }

    AdminContent()
}

@Composable
private fun AdminContent() {
    var waitlistEmails by remember { mutableStateOf(listOf<String>()) }
    var userEmails by remember { mutableStateOf(listOf<String>()) }
    var uniqueEmails by remember { mutableStateOf(listOf<String>()) }
    var waitlistOnlyEmails by remember { mutableStateOf(listOf<String>()) }
    var isLoading by remember { mutableStateOf(false) }
    var recomputeSummary by remember { mutableStateOf<String?>(null) }
    var isRecomputeLoading by remember { mutableStateOf(false) }
    var recomputeLogs by remember { mutableStateOf(listOf<String>()) }
    var backfillSummary by remember { mutableStateOf<String?>(null) }
    var isBackfillLoading by remember { mutableStateOf(false) }
    var backfillLogs by remember { mutableStateOf(listOf<String>()) }
    var emailProcessSummary by remember { mutableStateOf<String?>(null) }
    var isEmailProcessLoading by remember { mutableStateOf(false) }
    var emailProcessLogs by remember { mutableStateOf(listOf<String>()) }
    var waitlistEmailSummary by remember { mutableStateOf<String?>(null) }
    var isWaitlistEmailLoading by remember { mutableStateOf(false) }
    var waitlistEmailLogs by remember { mutableStateOf(listOf<String>()) }
    var createTemplate2Summary by remember { mutableStateOf<String?>(null) }
    var isCreateTemplate2Loading by remember { mutableStateOf(false) }
    var createTemplate2Logs by remember { mutableStateOf(listOf<String>()) }
    var createTemplate3Summary by remember { mutableStateOf<String?>(null) }
    var isCreateTemplate3Loading by remember { mutableStateOf(false) }
    var createTemplate3Logs by remember { mutableStateOf(listOf<String>()) }
    var usersSinceNovember2025CountsResult by remember {
        mutableStateOf<AdminOperationsRepository.CheckoutPaymentCountResult?>(null)
    }
    var isUsersSinceNovember2025CountsLoading by remember { mutableStateOf(false) }
    var usersSinceNovember2025CountsLogs by remember { mutableStateOf(listOf<String>()) }
    val scope = rememberCoroutineScope()

    Surface(Modifier.fillMaxSize().padding(24.px)) {
        Column(Modifier.fillMaxWidth(), horizontalAlignment = Alignment.Start) {
            SpanText(
                "Admin Operations",
                AppTheme.typography.h1.toModifier().color(AppTheme.colors.text)
            )

            Spacer(Modifier.padding(8.px))

            Button(
                onClick = {
                    scope.launch {
                        isLoading = true
                        try {
                            val result: AdminOperationsRepository.AdminEmailsResult =
                                AdminOperationsRepository.collectAllEmails()
                            waitlistEmails = result.waitlistEmails
                            userEmails = result.userEmails
                            uniqueEmails = result.uniqueEmails
                            waitlistOnlyEmails = result.waitlistOnlyEmails
                        } finally {
                            isLoading = false
                        }
                    }
                }
            ) {
                SpanText(if (isLoading) "Loading..." else "Collect Emails")
            }

            Spacer(Modifier.padding(16.px))
            EmailsSection(title = "Waitlist (waitlistMvp)", emails = waitlistEmails)
            Spacer(Modifier.padding(16.px))
            EmailsSection(title = "Users (users)", emails = userEmails)
            Spacer(Modifier.padding(16.px))
            EmailsSection(title = "Unique Combined", emails = uniqueEmails)
            Spacer(Modifier.padding(16.px))
            EmailsSection(title = "Waitlist Only (not in users)", emails = waitlistOnlyEmails)

            Spacer(Modifier.padding(16.px))

            Button(
                onClick = {
                    scope.launch {
                        isUsersSinceNovember2025CountsLoading = true
                        usersSinceNovember2025CountsLogs = emptyList()
                        usersSinceNovember2025CountsResult = null
                        try {
                            usersSinceNovember2025CountsResult =
                                AdminOperationsRepository.countUsersSinceNovember2025WithCheckoutAndPaymentByAcademicYear { msg ->
                                    scope.launch {
                                        usersSinceNovember2025CountsLogs = usersSinceNovember2025CountsLogs + msg
                                    }
                                }
                        } finally {
                            isUsersSinceNovember2025CountsLoading = false
                        }
                    }
                }
            ) {
                SpanText(
                    if (isUsersSinceNovember2025CountsLoading) {
                        "Counting..."
                    } else {
                        "Count checkout + payment users since November 2025"
                    }
                )
            }

            usersSinceNovember2025CountsResult?.let { result ->
                Spacer(Modifier.padding(8.px))
                CheckoutPaymentSinceNovember2025Section(result)
            }

            if (usersSinceNovember2025CountsLogs.isNotEmpty()) {
                Spacer(Modifier.padding(8.px))
                SpanText(
                    "Since November 2025 Count Logs (${usersSinceNovember2025CountsLogs.size})",
                    AppTheme.typography.body1Medium.toModifier().color(AppTheme.colors.text)
                )
                Spacer(Modifier.padding(4.px))
                usersSinceNovember2025CountsLogs.forEach { line ->
                    SpanText(
                        line,
                        AppTheme.typography.body2.toModifier().color(AppTheme.colors.textSecondary)
                    )
                }
            }

            Spacer(Modifier.padding(24.px))

            // Backfill templateId & quizId on all quizzes
            Button(
                onClick = {
                    scope.launch {
                        isBackfillLoading = true
                        backfillLogs = emptyList()
                        backfillSummary = null
                        try {
                            val summary = AdminOperationsRepository.backfillTemplateAndQuizIdForAllQuizzes { msg ->
                                scope.launch {
                                    backfillLogs = backfillLogs + msg
                                }
                            }
                            backfillSummary = summary
                        } finally {
                            isBackfillLoading = false
                        }
                    }
                }
            ) {
                SpanText(if (isBackfillLoading) "Processing..." else "Backfill templateId & quizId for All Quizzes")
            }

            if (!backfillSummary.isNullOrBlank()) {
                Spacer(Modifier.padding(8.px))
                SpanText(
                    "Summary: ${backfillSummary}",
                    AppTheme.typography.body1.toModifier().color(AppTheme.colors.textSecondary)
                )
            }

            if (backfillLogs.isNotEmpty()) {
                Spacer(Modifier.padding(8.px))
                SpanText(
                    "Backfill Logs (${backfillLogs.size})",
                    AppTheme.typography.body1Medium.toModifier().color(AppTheme.colors.text)
                )
                Spacer(Modifier.padding(4.px))
                backfillLogs.forEach { line ->
                    SpanText(
                        line,
                        AppTheme.typography.body2.toModifier().color(AppTheme.colors.textSecondary)
                    )
                }
            }

            Spacer(Modifier.padding(24.px))

            // Send emails to pre-generated recommendations
            Button(
                onClick = {
                    scope.launch {
                        isEmailProcessLoading = true
                        emailProcessLogs = emptyList()
                        emailProcessSummary = null
                        try {
                            val summary = AdminOperationsRepository.sendEmailsToPreGeneratedRecs { msg ->
                                scope.launch {
                                    emailProcessLogs = emailProcessLogs + msg
                                }
                            }
                            emailProcessSummary = summary
                        } finally {
                            isEmailProcessLoading = false
                        }
                    }
                }
            ) {
                SpanText(if (isEmailProcessLoading) "Processing..." else "Process All Users for Email Generation")
            }

            if (!emailProcessSummary.isNullOrBlank()) {
                Spacer(Modifier.padding(8.px))
                SpanText(
                    "Summary: ${emailProcessSummary}",
                    AppTheme.typography.body1.toModifier().color(AppTheme.colors.textSecondary)
                )
            }

            if (emailProcessLogs.isNotEmpty()) {
                Spacer(Modifier.padding(8.px))
                SpanText(
                    "Email Process Logs (${emailProcessLogs.size})",
                    AppTheme.typography.body1Medium.toModifier().color(AppTheme.colors.text)
                )
                Spacer(Modifier.padding(4.px))
                emailProcessLogs.forEach { line ->
                    SpanText(
                        line,
                        AppTheme.typography.body2.toModifier().color(AppTheme.colors.textSecondary)
                    )
                }
            }

            Spacer(Modifier.padding(24.px))

            // Send emails to waitlist-only users
            Button(
                onClick = {
                    scope.launch {
                        isWaitlistEmailLoading = true
                        waitlistEmailLogs = emptyList()
                        waitlistEmailSummary = null
                        try {
                            val summary = AdminOperationsRepository.sendEmailsToWaitlistOnlyUsers(
                                waitlistOnlyEmails = waitlistOnlyEmails
                            ) { msg ->
                                scope.launch {
                                    waitlistEmailLogs = waitlistEmailLogs + msg
                                }
                            }
                            waitlistEmailSummary = summary
                        } finally {
                            isWaitlistEmailLoading = false
                        }
                    }
                }
            ) {
                SpanText(if (isWaitlistEmailLoading) "Processing..." else "Send Emails to Waitlist-Only Users (${waitlistOnlyEmails.size})")
            }

            if (!waitlistEmailSummary.isNullOrBlank()) {
                Spacer(Modifier.padding(8.px))
                SpanText(
                    "Summary: ${waitlistEmailSummary}",
                    AppTheme.typography.body1.toModifier().color(AppTheme.colors.textSecondary)
                )
            }

            if (waitlistEmailLogs.isNotEmpty()) {
                Spacer(Modifier.padding(8.px))
                SpanText(
                    "Waitlist Email Logs (${waitlistEmailLogs.size})",
                    AppTheme.typography.body1Medium.toModifier().color(AppTheme.colors.text)
                )
                Spacer(Modifier.padding(4.px))
                waitlistEmailLogs.forEach { line ->
                    SpanText(
                        line,
                        AppTheme.typography.body2.toModifier().color(AppTheme.colors.textSecondary)
                    )
                }
            }

            Spacer(Modifier.padding(24.px))

// Create Quiz Template 2
            Button(
                onClick = {
                    scope.launch {
                        isCreateTemplate2Loading = true
                        createTemplate2Logs = emptyList()
                        createTemplate2Summary = null
                        try {
                            val summary = AdminOperationsRepository.createQuizTemplate2 { msg ->
                                scope.launch {
                                    createTemplate2Logs = createTemplate2Logs + msg
                                }
                            }
                            createTemplate2Summary = summary
                        } finally {
                            isCreateTemplate2Loading = false
                        }
                    }
                }
            ) {
                SpanText(if (isCreateTemplate2Loading) "Processing..." else "Create Quiz Template 2")
            }

            if (!createTemplate2Summary.isNullOrBlank()) {
                Spacer(Modifier.padding(8.px))
                SpanText(
                    "Summary: ${createTemplate2Summary}",
                    AppTheme.typography.body1.toModifier().color(AppTheme.colors.textSecondary)
                )
            }

            if (createTemplate2Logs.isNotEmpty()) {
                Spacer(Modifier.padding(8.px))
                SpanText(
                    "Create Template 2 Logs (${createTemplate2Logs.size})",
                    AppTheme.typography.body1Medium.toModifier().color(AppTheme.colors.text)
                )
                Spacer(Modifier.padding(4.px))
                createTemplate2Logs.forEach { line ->
                    SpanText(
                        line,
                        AppTheme.typography.body2.toModifier().color(AppTheme.colors.textSecondary)
                    )
                }
            }

            Spacer(Modifier.padding(24.px))

            // Create Quiz Template 3 from 2026 matching specialties CSV
            Button(
                onClick = {
                    scope.launch {
                        isCreateTemplate3Loading = true
                        createTemplate3Logs = emptyList()
                        createTemplate3Summary = null
                        try {
                            val summary = AdminOperationsRepository.createQuizTemplate3FromMatchingSpecialtiesCsv { msg ->
                                scope.launch {
                                    createTemplate3Logs = createTemplate3Logs + msg
                                }
                            }
                            createTemplate3Summary = summary
                        } finally {
                            isCreateTemplate3Loading = false
                        }
                    }
                }
            ) {
                SpanText(if (isCreateTemplate3Loading) "Processing..." else "Create Quiz Template 3")
            }

            if (!createTemplate3Summary.isNullOrBlank()) {
                Spacer(Modifier.padding(8.px))
                SpanText(
                    "Summary: ${createTemplate3Summary}",
                    AppTheme.typography.body1.toModifier().color(AppTheme.colors.textSecondary)
                )
            }

            if (createTemplate3Logs.isNotEmpty()) {
                Spacer(Modifier.padding(8.px))
                SpanText(
                    "Create Template 3 Logs (${createTemplate3Logs.size})",
                    AppTheme.typography.body1Medium.toModifier().color(AppTheme.colors.text)
                )
                Spacer(Modifier.padding(4.px))
                createTemplate3Logs.forEach { line ->
                    SpanText(
                        line,
                        AppTheme.typography.body2.toModifier().color(AppTheme.colors.textSecondary)
                    )
                }
            }
        }
    }
}

@Composable
private fun CheckoutPaymentSinceNovember2025Section(
    result: AdminOperationsRepository.CheckoutPaymentCountResult
) {
    Column(Modifier.fillMaxWidth()) {
        SpanText(
            "Users created since November 1, 2025: ${result.usersInDateRange}; checkout starts: ${result.usersWithCheckoutSession}; successful payments: ${result.usersWithSuccessfulPayment}; errors: ${result.errors}",
            AppTheme.typography.body1.toModifier().color(AppTheme.colors.textSecondary)
        )
        Spacer(Modifier.padding(8.px))

        Table(attrs = Modifier.fillMaxWidth().toAttrs()) {
            Thead {
                Tr {
                    Th(
                        attrs = AppTheme.typography.body1Medium.toModifier()
                            .textAlign(TextAlign.Left)
                            .fontWeight(FontWeight.Bold)
                            .toAttrs()
                    ) {
                        SpanText("Academic Year")
                    }
                    Th(
                        attrs = AppTheme.typography.body1Medium.toModifier()
                            .textAlign(TextAlign.Left)
                            .fontWeight(FontWeight.Bold)
                            .toAttrs()
                    ) {
                        SpanText("Checkout Starts")
                    }
                    Th(
                        attrs = AppTheme.typography.body1Medium.toModifier()
                            .textAlign(TextAlign.Left)
                            .fontWeight(FontWeight.Bold)
                            .toAttrs()
                    ) {
                        SpanText("Successful Payments")
                    }
                }
            }
            Tbody {
                if (result.countsByAcademicYear.isEmpty()) {
                    Tr {
                        Td { SpanText("No matching users.", AppTheme.typography.body1.toModifier()) }
                        Td { SpanText("0", AppTheme.typography.body1.toModifier()) }
                        Td { SpanText("0", AppTheme.typography.body1.toModifier()) }
                    }
                } else {
                    result.countsByAcademicYear.forEach { row ->
                        Tr {
                            Td { SpanText(row.academicYear, AppTheme.typography.body1.toModifier()) }
                            Td { SpanText(row.checkoutStarts.toString(), AppTheme.typography.body1.toModifier()) }
                            Td { SpanText(row.successfulPayments.toString(), AppTheme.typography.body1.toModifier()) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun EmailsSection(title: String, emails: List<String>) {
    Column(Modifier.fillMaxWidth()) {
        Row(Modifier.fillMaxWidth()) {
            SpanText(
                "$title — count: ${emails.size}",
                AppTheme.typography.body1Medium.toModifier().color(AppTheme.colors.text)
            )
        }
        Spacer(Modifier.padding(8.px))
        if (emails.isEmpty()) {
            SpanText(
                "No emails.",
                AppTheme.typography.body1.toModifier().color(AppTheme.colors.textSecondary)
            )
        } else {
            emails.forEach { email: String ->
                SpanText(
                    email,
                    AppTheme.typography.body1.toModifier().color(AppTheme.colors.textSecondary)
                )
            }
        }
    }
}
